import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import './Header.css';
import logoIcon from '../assets/logo.gif';

const Header = ({ showSearch = true }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isShuffling, setIsShuffling] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchContainerRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchSuggestions = async () => {
      if (searchTerm.trim().length > 0) {
        try {
          const results = await api.getWordSuggestions(searchTerm);
          setSuggestions(results);
          setShowSuggestions(true);
        } catch (error) {
          console.error('Error fetching suggestions:', error);
        }
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    };

    const debounceTimer = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchTerm]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      navigate(`/word/${searchTerm.toLowerCase().trim()}`);
      setSearchTerm('');
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (word) => {
    navigate(`/word/${word}`);
    setSearchTerm('');
    setShowSuggestions(false);
  };

  const handleShuffle = async () => {
    if (isShuffling) return;
    
    try {
      setIsShuffling(true);
      const randomWord = await api.getRandomWord();
      console.log('Got random word:', randomWord);
      
      const newPath = `/word/${randomWord}`;
      
      navigate(newPath);
      window.location.reload();
      
    } catch (error) {
      console.error('Error getting random word:', error);
    } finally {
      setTimeout(() => {
        setIsShuffling(false);
      }, 500);
    }
  };

  return (
    <header className="header-container">
      <Link to="/" className="logo-container">
        <h1 className="logo">
          <span className="logo-text">The Learning</span>
          <span className="logo-text dictionary">Dictionary</span>
          <span className="logo-icon">
            <img src={logoIcon} alt="Learning Dictionary Logo" />
          </span>
        </h1>
        <p className="tagline">Words, but cooler</p>
      </Link>

      {showSearch && (
        <div className="search-container" ref={searchContainerRef}>
          <form onSubmit={handleSubmit}>
            <div className="search-input-wrapper">
              <input
                type="text"
                placeholder="Look up a word"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
                onFocus={() => searchTerm.trim() && setShowSuggestions(true)}
              />
              <button type="submit" className="search-button">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
                </svg>
              </button>
              <button 
                type="button" 
                className={`shuffle-button ${isShuffling ? 'shuffling' : ''}`} 
                onClick={handleShuffle}
                disabled={isShuffling}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M10.59 9.17L5.41 4 4 5.41l5.17 5.17 1.42-1.41zM14.5 4l2.04 2.04L4 18.59 5.41 20 17.96 7.46 20 9.5V4h-5.5zm0.33 9.41l-1.41 1.41 3.13 3.13L14.5 20H20v-5.5l-2.04 2.04-3.13-3.13z" />
                </svg>
              </button>
            </div>
            {showSuggestions && suggestions.length > 0 && (
              <div className="search-suggestions">
                {suggestions.map((suggestion, index) => (
                  <div
                    key={index}
                    className="suggestion-item"
                    onClick={() => handleSuggestionClick(suggestion)}
                  >
                    {suggestion}
                  </div>
                ))}
              </div>
            )}
          </form>
        </div>
      )}
    </header>
  );
};

export default Header; 